import React from "react";
import { IOrganisation } from "../../services/nestjs-base-api/types";
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Divider, 
  Tag, 
  List,
  Space,
  Alert,
  Button,
  Tooltip,
  Modal
} from "antd";
import { 
  BuildOutlined, 
  SafetyCertificateOutlined, 
  SolutionOutlined, 
  TeamOutlined, 
  LockOutlined,
  CloudUploadOutlined,
  BarChartOutlined,
  ApiOutlined,
  InfoCircleOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { PlanLevelEnum } from "../../enums/PlanLevelEnum";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

const { Title, Text, Paragraph } = Typography;

interface IProps {
  organisation: IOrganisation;
}

const OrganisationDetails: React.FunctionComponent<IProps> = (props) => {
  const { organisation } = props;
  const intl = useIntl();
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = React.useState(false);

  // Handle case when organisation is undefined or missing required properties
  if (!organisation || !organisation.plan) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Text type="secondary">
          {intl.formatMessage({ id: 'organisation.loading' }) || 'Loading organisation details...'}
        </Text>
      </div>
    );
  }

  // Ensure the organisation has a plan property
  const orgPlan = organisation.plan || PlanLevelEnum.Plan1;
  
  console.log('OrganisationDetails component received organisation:', organisation);
  
  // Function to get plan name based on enum
  const getPlanName = (plan: PlanLevelEnum): string => {
    switch (plan) {
      case PlanLevelEnum.Plan1:
        return intl.formatMessage({ id: 'organisation.plan.basic' }) || 'Basic';
      case PlanLevelEnum.Plan2:
        return intl.formatMessage({ id: 'organisation.plan.standard' }) || 'Standard';
      case PlanLevelEnum.Plan3:
        return intl.formatMessage({ id: 'organisation.plan.professional' }) || 'Professional';
      case PlanLevelEnum.Plan4:
        return intl.formatMessage({ id: 'organisation.plan.enterprise' }) || 'Enterprise';
      default:
        return String(plan) || 'Basic';
    }
  };

  // Function to get plan color based on enum
  const getPlanColor = (plan: PlanLevelEnum): string => {
    switch (plan) {
      case PlanLevelEnum.Plan1:
        return 'blue';
      case PlanLevelEnum.Plan2:
        return 'cyan';
      case PlanLevelEnum.Plan3:
        return 'purple';
      case PlanLevelEnum.Plan4:
        return 'gold';
      default:
        return 'default';
    }
  };

  // Features data based on plan levels
  const planFeatures = [
    {
      title: intl.formatMessage({ id: 'organisation.features.faceRecognition' }) || 'Face Recognition',
      description: intl.formatMessage({ id: 'organisation.features.faceRecognitionDesc' }) || 'Match attendees to photos quickly with AI-powered facial recognition.',
      plans: [PlanLevelEnum.Plan1, PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4],
      icon: <BuildOutlined />
    },
    {
      title: intl.formatMessage({ id: 'organisation.features.galleries' }) || 'Photo Galleries',
      description: intl.formatMessage({ id: 'organisation.features.galleriesDesc' }) || 'Organize your event photos into distinct galleries for easy management.',
      plans: [PlanLevelEnum.Plan1, PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4],
      icon: <SolutionOutlined />
    },
    {
      title: intl.formatMessage({ id: 'organisation.features.teamMembers' }) || 'Team Members',
      description: intl.formatMessage({ id: 'organisation.features.teamMembersDesc' }) || 'Collaborate with team members with role-based permissions.',
      limit: {
        [PlanLevelEnum.Plan1]: '3 members',
        [PlanLevelEnum.Plan2]: '10 members',
        [PlanLevelEnum.Plan3]: '25 members',
        [PlanLevelEnum.Plan4]: 'Unlimited',
      },
      plans: [PlanLevelEnum.Plan1, PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4],
      icon: <TeamOutlined />
    },
    {
      title: intl.formatMessage({ id: 'organisation.features.secureStorage' }) || 'Secure Photo Storage',
      description: intl.formatMessage({ id: 'organisation.features.secureStorageDesc' }) || 'All photos are securely stored with enterprise-grade encryption.',
      limit: {
        [PlanLevelEnum.Plan1]: '5 GB',
        [PlanLevelEnum.Plan2]: '20 GB',
        [PlanLevelEnum.Plan3]: '100 GB',
        [PlanLevelEnum.Plan4]: 'Custom',
      },
      plans: [PlanLevelEnum.Plan1, PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4],
      icon: <SafetyCertificateOutlined />
    },
    {
      title: intl.formatMessage({ id: 'organisation.features.advancedSecurity' }) || 'Advanced Security Controls',
      description: intl.formatMessage({ id: 'organisation.features.advancedSecurityDesc' }) || 'Enhanced security features including 2FA and audit logs.',
      plans: [PlanLevelEnum.Plan3, PlanLevelEnum.Plan4],
      icon: <LockOutlined />
    },
    {
      title: intl.formatMessage({ id: 'organisation.features.bulkUpload' }) || 'Bulk Upload Tools',
      description: intl.formatMessage({ id: 'organisation.features.bulkUploadDesc' }) || 'Upload thousands of photos at once with our advanced upload tools.',
      plans: [PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4],
      icon: <CloudUploadOutlined />
    },
    {
      title: intl.formatMessage({ id: 'organisation.features.analytics' }) || 'Analytics Dashboard',
      description: intl.formatMessage({ id: 'organisation.features.analyticsDesc' }) || 'Gain insights into your events and photo engagement metrics.',
      plans: [PlanLevelEnum.Plan3, PlanLevelEnum.Plan4],
      icon: <BarChartOutlined />
    },
    {
      title: intl.formatMessage({ id: 'organisation.features.apiAccess' }) || 'API Access',
      description: intl.formatMessage({ id: 'organisation.features.apiAccessDesc' }) || 'Integrate our services with your existing systems via our comprehensive API.',
      plans: [PlanLevelEnum.Plan4],
      icon: <ApiOutlined />
    },
  ];

  // Best practice tips based on plan
  const bestPractices = [
    {
      title: intl.formatMessage({ id: 'organisation.bestPractices.eventPlanning' }) || 'Plan your events in advance',
      description: intl.formatMessage({ id: 'organisation.bestPractices.eventPlanningDesc' }) || 'Create events at least one week before they happen to ensure smooth setup and preparation.',
      plans: [PlanLevelEnum.Plan1, PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4]
    },
    {
      title: intl.formatMessage({ id: 'organisation.bestPractices.photosQuality' }) || 'Ensure quality photo collection',
      description: intl.formatMessage({ id: 'organisation.bestPractices.photosQualityDesc' }) || 'Use high-resolution photos (at least 1080p) with good lighting for best recognition results.',
      plans: [PlanLevelEnum.Plan1, PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4]
    },
    {
      title: intl.formatMessage({ id: 'organisation.bestPractices.multipleGalleries' }) || 'Use multiple galleries',
      description: intl.formatMessage({ id: 'organisation.bestPractices.multipleGalleriesDesc' }) || 'Organize photos into separate galleries based on event sections, VIPs, or timeframes.',
      plans: [PlanLevelEnum.Plan1, PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4]
    },
    {
      title: intl.formatMessage({ id: 'organisation.bestPractices.shareQrCodes' }) || 'Share event QR codes',
      description: intl.formatMessage({ id: 'organisation.bestPractices.shareQrCodesDesc' }) || 'Display QR codes prominently at your event for attendees to quickly access their photos.',
      plans: [PlanLevelEnum.Plan1, PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4]
    },
    {
      title: intl.formatMessage({ id: 'organisation.bestPractices.securitySettings' }) || 'Review security settings',
      description: intl.formatMessage({ id: 'organisation.bestPractices.securitySettingsDesc' }) || 'Regularly audit user permissions and security settings, especially after team changes.',
      plans: [PlanLevelEnum.Plan2, PlanLevelEnum.Plan3, PlanLevelEnum.Plan4]
    },
    {
      title: intl.formatMessage({ id: 'organisation.bestPractices.backupExport' }) || 'Schedule regular exports',
      description: intl.formatMessage({ id: 'organisation.bestPractices.backupExportDesc' }) || 'Periodically export your most important galleries as a backup precaution.',
      plans: [PlanLevelEnum.Plan3, PlanLevelEnum.Plan4]
    }
  ];

  return (
    <div className="organisation-details">
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title={intl.formatMessage({ id: 'organisation.information' }) || 'Organisation Information'}>
            <div style={{ marginBottom: 16 }}>
              <Title level={4}>{organisation.name}</Title>
              <Tag color={getPlanColor(orgPlan)}>
                {getPlanName(orgPlan)} {intl.formatMessage({ id: 'organisation.plan' }) || 'Plan'}
              </Tag>
            </div>
            
            <Divider />
            
            <Title level={5}>
              {intl.formatMessage({ id: 'organisation.currentFeatures' }) || 'Current Plan Features'}
            </Title>
            
            <List
              itemLayout="horizontal"
              dataSource={planFeatures.filter(feature => feature.plans.includes(orgPlan))}
              renderItem={item => (
                <List.Item>
                  <List.Item.Meta
                    avatar={item.icon}
                    title={<Space>
                      {item.title}
                      {item.limit && 
                        <Tooltip title={intl.formatMessage({ id: 'organisation.limitInfo' }) || 'Limit for your plan'}>
                          <Tag color="blue">{item.limit[orgPlan]}</Tag>
                        </Tooltip>
                      }
                    </Space>}
                    description={item.description}
                  />
                </List.Item>
              )}
            />

            {orgPlan !== PlanLevelEnum.Plan4 && (
              <>
                <Divider />
                <Title level={5}>
                  {intl.formatMessage({ id: 'organisation.upgradeBenefits' }) || 'Upgrade Benefits'}
                </Title>
                
                <List
                  itemLayout="horizontal"
                  dataSource={planFeatures.filter(feature => !feature.plans.includes(orgPlan))}
                  renderItem={item => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={item.icon}
                        title={<Space>
                          {item.title}
                          <Tag color="volcano">
                            {intl.formatMessage({ id: 'organisation.upgradeable' }) || 'Upgrade to Unlock'}
                          </Tag>
                        </Space>}
                        description={item.description}
                      />
                    </List.Item>
                  )}
                />
                
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Button type="primary">
                    {intl.formatMessage({ id: 'organisation.upgradeNow' }) || 'Upgrade Your Plan'}
                  </Button>
                </div>
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={intl.formatMessage({ id: 'organisation.bestPractices' }) || 'Best Practices'}>
            <Alert
              message={intl.formatMessage({ id: 'organisation.bestPracticesAlert' }) || 'Optimize Your Experience'}
              description={intl.formatMessage({ id: 'organisation.bestPracticesAlertDesc' }) || 'Follow these best practices to get the most out of Recognix for your events.'}
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
            />
            
            <List
              itemLayout="vertical"
              dataSource={bestPractices.filter(practice => practice.plans.includes(orgPlan))}
              renderItem={item => (
                <List.Item>
                  <List.Item.Meta
                    title={item.title}
                  />
                  <Paragraph>{item.description}</Paragraph>
                </List.Item>
              )}
            />
          </Card>
          
          <Card 
            title={intl.formatMessage({ id: 'organisation.quickLinks' }) || 'Quick Links'} 
            style={{ marginTop: 24 }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Button 
                  block 
                  icon={<TeamOutlined />}
                  onClick={() => navigate('/dashboard/organisation')}
                >
                  {intl.formatMessage({ id: 'organisation.manageUsers' }) || 'Manage Users'}
                </Button>
              </Col>
              <Col span={12}>
                <Button 
                  block 
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/dashboard/events')}
                >
                  {intl.formatMessage({ id: 'organisation.createEvent' }) || 'Create Event'}
                </Button>
              </Col>
              <Col span={12}>
                <Button 
                  block 
                  icon={<BuildOutlined />}
                  onClick={() => {
                    Modal.info({
                      title: intl.formatMessage({ id: 'common.comingSoon' }) || 'Coming Soon',
                      content: intl.formatMessage({ id: 'common.featureComingSoon' }) || 'This feature is coming soon. Stay tuned for updates!'
                    });
                  }}
                >
                  {intl.formatMessage({ id: 'organisation.settings' }) || 'Settings'}
                </Button>
              </Col>
              <Col span={12}>
                <Button 
                  block 
                  icon={<BarChartOutlined />}
                  onClick={() => {
                    Modal.info({
                      title: intl.formatMessage({ id: 'common.comingSoon' }) || 'Coming Soon',
                      content: intl.formatMessage({ id: 'common.featureComingSoon' }) || 'This feature is coming soon. Stay tuned for updates!'
                    });
                  }}
                >
                  {intl.formatMessage({ id: 'organisation.viewStats' }) || 'View Stats'}
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default OrganisationDetails;
